import React from "react";
import {
  FiChevronDown,
  FiChevronUp,
  FiClock,
  FiExternalLink,
  FiGlobe,
  FiMail,
  FiMap,
  FiMapPin,
  FiPhone,
} from "react-icons/fi";
import { HiOfficeBuilding } from "react-icons/hi";

import Reveal from "../../../components/ui/Reveal";

import ServiceContactLink from "./ServiceContactLink";
import ServiceMapPanel from "./ServiceMapPanel";

import { cn } from "../utils/servicesMunicipauxUtils";

function InfoLine({ icon: Icon, children }) {
  if (!children) return null;

  return (
    <div className="flex items-start gap-3 text-sm font-semibold text-slate-600 dark:text-slate-300">
      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-white text-[#1277B9] shadow-sm dark:bg-slate-950">
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 pt-2">{children}</div>
    </div>
  );
}

export default function ServiceCard({
  service,
  isMapOpen = false,
  isDetailsOpen = false,
  onToggleMap,
  onToggleDetails,
  delay = 0,
}) {
  return (
    <Reveal delay={delay}>
      <article className="group overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:border-[#1277B9]/50 hover:shadow-xl dark:border-slate-800 dark:bg-slate-950">
        <div className="h-2 bg-gradient-to-r from-[#1277B9] via-sky-400 to-cyan-300" />

        <div className="grid gap-5 p-5 lg:grid-cols-[1.1fr_0.9fr] sm:p-6">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              {service.category && (
                <span className="inline-flex rounded-full bg-[#1277B9]/10 px-3 py-1 text-xs font-bold text-[#1277B9] dark:bg-[#1277B9]/20 dark:text-sky-300">
                  {service.category}
                </span>
              )}

              {service.type && (
                <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-bold text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
                  {service.type}
                </span>
              )}
            </div>

            <div className="mt-4 flex items-start gap-3">
              <span className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#1277B9]/10 text-[#1277B9] dark:bg-[#1277B9]/20 dark:text-sky-300 sm:inline-flex">
                <HiOfficeBuilding className="h-6 w-6" />
              </span>

              <div className="min-w-0">
                <h2 className="text-2xl font-bold leading-tight text-slate-950 dark:text-white">
                  {service.title}
                </h2>

                {service.accroche && (
                  <p className="mt-2 text-sm font-bold leading-6 text-[#1277B9] dark:text-sky-300">
                    {service.accroche}
                  </p>
                )}
              </div>
            </div>

            {service.descriptionPreview && (
              <p className="mt-4 text-sm leading-7 text-slate-600 dark:text-slate-300">
                {service.descriptionPreview}
              </p>
            )}

            <div className="mt-5 flex flex-wrap gap-2">
              <ServiceContactLink
                href={service.phoneHref}
                icon={FiPhone}
                label={service.phone || "Téléphoner"}
                variant="primary"
              />

              {service.emails.slice(0, 1).map((mail) => (
                <ServiceContactLink
                  key={mail}
                  href={`mailto:${mail}`}
                  icon={FiMail}
                  label="Email"
                />
              ))}

              <ServiceContactLink
                href={service.websiteUrl}
                icon={FiGlobe}
                label="Site web"
                external
              />

              <ServiceContactLink
                href={service.otherUrl}
                icon={FiExternalLink}
                label="Lien utile"
                external
              />

              {service.hasMap && (
                <button
                  type="button"
                  onClick={onToggleMap}
                  className={cn(
                    "inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl border px-4 py-2 text-sm font-bold transition",
                    isMapOpen
                      ? "border-[#1277B9] bg-[#1277B9] text-white shadow-sm hover:bg-[#0f659d]"
                      : "border-slate-200 bg-white text-slate-700 hover:border-[#1277B9]/50 hover:bg-sky-50 hover:text-[#1277B9] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
                  )}
                >
                  <FiMap className="h-4 w-4" />
                  {isMapOpen ? "Masquer la carte" : "Voir la carte"}
                </button>
              )}

              {(service.descriptionRaw || service.infos) && (
                <button
                  type="button"
                  onClick={onToggleDetails}
                  className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:border-[#1277B9]/50 hover:bg-sky-50 hover:text-[#1277B9] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
                >
                  {isDetailsOpen ? (
                    <FiChevronUp className="h-4 w-4" />
                  ) : (
                    <FiChevronDown className="h-4 w-4" />
                  )}
                  {isDetailsOpen ? "Masquer les détails" : "Plus d’infos"}
                </button>
              )}
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/70">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              Contact & infos pratiques
            </p>

            <div className="mt-4 space-y-3">
              {service.phone && (
                <InfoLine icon={FiPhone}>
                  <a
                    href={service.phoneHref}
                    className="hover:text-[#1277B9] hover:underline"
                  >
                    {service.phone}
                  </a>
                </InfoLine>
              )}

              {service.phoneSecondary && (
                <InfoLine icon={FiPhone}>
                  <a
                    href={service.phoneSecondaryHref}
                    className="hover:text-[#1277B9] hover:underline"
                  >
                    {service.phoneSecondary}
                  </a>
                </InfoLine>
              )}

              {service.emails.map((mail) => (
                <InfoLine key={mail} icon={FiMail}>
                  <a
                    href={`mailto:${mail}`}
                    className="break-all text-[#1277B9] hover:underline"
                  >
                    {mail}
                  </a>
                </InfoLine>
              ))}

              {service.address && (
                <InfoLine icon={FiMapPin}>
                  {service.mapsHref ? (
                    <a
                      href={service.mapsHref}
                      target="_blank"
                      rel="noreferrer"
                      className="whitespace-pre-line hover:text-[#1277B9] hover:underline"
                    >
                      {service.address}
                    </a>
                  ) : (
                    <span className="whitespace-pre-line">
                      {service.address}
                    </span>
                  )}
                </InfoLine>
              )}

              {service.horaires && (
                <InfoLine icon={FiClock}>
                  <span className="whitespace-pre-line">
                    {service.horaires}
                  </span>
                </InfoLine>
              )}
            </div>
          </div>
        </div>

        {(isDetailsOpen || isMapOpen) && (
          <div className="px-5 pb-5 sm:px-6 sm:pb-6">
            {isDetailsOpen && (
              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-900/70">
                <h3 className="text-lg font-bold text-slate-950 dark:text-white">
                  Informations complémentaires
                </h3>

                {service.descriptionText && (
                  <p className="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">
                    {service.descriptionText}
                  </p>
                )}

                {service.infos && (
                  <p className="mt-4 whitespace-pre-line border-t border-dashed border-slate-200 pt-4 text-sm leading-7 text-slate-600 dark:border-slate-700 dark:text-slate-300">
                    {service.infos}
                  </p>
                )}
              </div>
            )}

            {isMapOpen && <ServiceMapPanel service={service} />}
          </div>
        )}
      </article>
    </Reveal>
  );
}